"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { SupportNavigation } from "@/app/modules/support/components/SupportNavigation";
import { SupportAccountProvider } from "@/app/modules/support/components/SupportAccountProvider";
import { supportPageKey } from "@/app/modules/support/navigation";
import { Flash } from "../Flash";
import { AuthScreen } from "../workspace/AuthScreen";
import { TakeresWordmark } from "../workspace/TakeresWordmark";
import { WorkspaceControlPocket } from "../workspace/WorkspaceControlPocket";
import { WorkspaceProfileMenu } from "../workspace/WorkspaceProfileMenu";
import { useAccountSession } from "./hooks/useAccountSession";

export function SupportAccountShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("support.navigation");
  const common = useTranslations("common");
  const session = useAccountSession(router);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const pageTitle = t(supportPageKey(pathname));
  const documentTitle = session.loading || !session.user
    ? t("title")
    : t("documentTitle", { page: pageTitle, title: t("title") });

  useLayoutEffect(() => {
    const applyTitle = () => {
      if (document.title !== documentTitle) document.title = documentTitle;
    };

    applyTitle();
    const observer = new MutationObserver(applyTitle);
    observer.observe(document.head, { childList: true, characterData: true, subtree: true });

    return () => observer.disconnect();
  }, [documentTitle]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  useEffect(() => {
    if (!profileOpen) return;
    function closeOutside(event: MouseEvent) {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setProfileOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setProfileOpen(false);
    }
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileOpen]);

  if (session.loading) {
    return <main className="grid min-h-[var(--app-viewport-height)] place-items-center bg-bg" aria-busy="true"><p role="status" className="text-sm text-text-secondary">{common("loading")}</p></main>;
  }

  if (!session.user) {
    return <AuthScreen onAuthenticated={session.reload} run={session.run} busy={session.busy} error={session.error} initialMode="login" contextDescription={t("authDescription")} />;
  }

  return (
    <div className="flex h-[var(--app-viewport-height)] min-h-0 flex-col overflow-hidden bg-surface md:flex-row">
      <WorkspaceControlPocket>
        <WorkspaceProfileMenu user={session.user} busy={session.busy} open={profileOpen} menuRef={menuRef} onToggle={() => setProfileOpen((open) => !open)} onClose={() => setProfileOpen(false)} onLogout={session.logout} onLocaleChanged={session.reload} />
      </WorkspaceControlPocket>
      <aside className="shrink-0 bg-surface md:flex md:min-h-0 md:w-[240px] md:flex-col lg:w-[296px]">
        <div className="flex h-14 shrink-0 items-center gap-3 px-3 pr-24 sm:px-5 sm:pr-24 md:h-32 md:px-5">
          <Link href="/" aria-label={t("workspaces")} title={t("workspaces")} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="m9 5-7 7 7 7M2 12h20" /></svg>
          </Link>
          <div className="flex min-w-0 flex-col gap-2">
            <Link href="/" className="hidden self-start rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:block"><TakeresWordmark label={common("brand")} size="xs" /></Link>
            <Link href="/support" className="truncate rounded-lg text-lg font-bold tracking-tight text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t("shortTitle")}</Link>
          </div>
        </div>
        <SupportNavigation />
      </aside>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col rounded-[var(--app-frame-radius)] bg-[var(--app-canvas-color)]">
        <header className="shrink-0 px-4 pb-5 pt-6 sm:px-6 md:min-h-28 md:pr-28 md:pt-9 lg:px-8 lg:pr-32">
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-text lg:text-[30px]">{pageTitle}</h1>
          <p className="mt-2 text-sm text-text-secondary">{t("description")}</p>
        </header>
        <main ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 sm:px-6 lg:px-8 lg:pb-8">
          <Flash error={session.error} message={session.message} />
          <SupportAccountProvider key={session.user.uuid} userUuid={session.user.uuid}>{children}</SupportAccountProvider>
        </main>
      </div>
    </div>
  );
}
